'use client';

import { useCallback, useState } from 'react';

import { Input } from '@kit/ui/input';

import { isValidE164 } from '../utils/phone';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name: string;
  placeholder?: string;
  required?: boolean;
  'aria-invalid'?: boolean;
  className?: string;
}

/**
 * Phone input with E.164 validation on blur.
 * Built on @kit/ui Input component.
 * Shows an inline error message when the number is invalid.
 */
export function PhoneInput({
  value,
  onChange,
  onBlur,
  name,
  placeholder = '+1 (555) 123-4567',
  required,
  'aria-invalid': ariaInvalid,
  className,
}: PhoneInputProps) {
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const validatePhone = useCallback((val: string): boolean => {
    if (val && val.trim().length > 0) {
      const normalized = val.startsWith('+') ? val : `+${val}`;

      if (!isValidE164(normalized)) {
        setPhoneError(
          'Please enter a valid phone number with country code (e.g. +1 555 123 4567)',
        );
        return false;
      }

      setPhoneError(null);
      return true;
    }

    setPhoneError(null);
    return true;
  }, []);

  const handleBlur = useCallback(() => {
    validatePhone(value);
    onBlur?.();
  }, [value, onBlur, validatePhone]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Clear error when user starts typing again
      if (phoneError) {
        setPhoneError(null);
      }

      onChange(e.target.value);
    },
    [onChange, phoneError],
  );

  return (
    <div className="flex flex-col gap-1">
      <Input
        type="tel"
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        aria-invalid={ariaInvalid || !!phoneError}
        className={className}
        data-test="phone-input"
      />

      {phoneError ? (
        <p className="text-destructive text-[0.8rem] font-medium">
          {phoneError}
        </p>
      ) : null}
    </div>
  );
}
